import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import { DateRangePicker, Stack } from "rsuite";
import { BiSearch, BiReset } from "react-icons/bi";
import { BsCloudDownloadFill } from "react-icons/bs";
import Sidebar from "../../includes/Sidebar";
import { downloadXl, predefinedBottomRanges, convertDateToStr } from "../../../functions";
import { Tooltip } from "@mui/material";
import { seErrorMessage } from "../../../slice/error/errorSlice";
import { useDispatch } from "react-redux";
import { SuperQaReportService } from "../../../services/SuperQaReportService";
import { DataListService } from "../../../services/DataListService";
import Select from "react-select";
import useCacheFilter from "../../../utils/useCacheFilter";
import PartnerDropDown from "../../components/PartnerDropDown";
import ClientDropDown from "../../components/ClientDropDown";
import FacilityDropDown from "../../components/FacilityDropDown";
import startOfMonth from "date-fns/startOfMonth";


const datatableCustomStyles = {
  rows: {
    style: {
      minHeight: '20px',
      fontSize: '0.7em',
      fontColor: '#212529 !important',
      width: '100%'
    },
  },
  headCells: {
    style: {
      paddingLeft: '5px',
      paddingRight: '5px',
      textTransform: "uppercase",
      fontSize: '0.7em',
      fontWeight: '800',
      fontColor: '#6c757d !important',
      opacity: 1,
      minHeight: "10px",
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      width: "100%",
    },
  },
  pagination: {
    style: {
      color: '#000',
      fontSize: '13px',
      minHeight: '56px',
      backgroundColor: '#fff',
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
      borderTopColor: '#f2f2f2',
    },
    pageButtonsStyle: {
      borderRadius: '50%',
      height: '40px',
      width: '40px',
      padding: '8px',
      margin: '0px',
      cursor: 'pointer',
      transition: '0.4s',
      color: '#000',
      fill: '#000',
      backgroundColor: 'transparent',
      '&:disabled': {
        cursor: 'unset',
        color: '#e2e2e2',
        fill: '#e2e2e2',
      },
      '&:hover:not(:disabled)': {
        backgroundColor: '#f2f2f2',
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: '#f5f5f5',
      },
    },
  },
};



const filterTypes = [
  { value: "jobwise", label: "Job Wise" },
  { value: "claimwise", label: "Claim Wise" },
  {value: "index_mismatch", label: "Index Mismatch" },
  {value:"plbwise", label: "PLB Wise"},
  {value:"sl_wise", label: "SL Wise"},
];

const statuses = [
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
  {value: "Cancelled", label: "Cancelled"},
  {value:" Auto Closed", label: "Auto Closed"},
];

const selectStyles = {
  container: (styles) => ({
    ...styles,
    borderRadius: "9px",
    width: "100%"
  }),
  control: (styles, state) => ({
    ...styles,
    backgroundColor: "hsl(204deg 33.33% 97.06%)",
    borderStyle: "none",
    boxShadow: "none",
    borderRadius: "10px",
    maxHeight: "30px",
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    width: "0px",
    minHeight: "20px",
    maxHeight: "30px",
  }),
  valueContainer: (styles) => ({
    ...styles,
    maxHeight: "30px",
    overflow: "scroll",
  }),
  placeholder: (styles) => ({
    ...styles,
    fontSize: "12px"
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? "#dbf4f5" : "inherit",
    color: state.isSelected ? "#000000" : "inherit",
    "&:hover": { backgroundColor: state.isSelected ? "#dbf4f5" : "rgb(222, 235, 255)" },
  }),
};

// Processed Date defaults to the current month, same as Payer Crosswalk
const getInitialFilterValues = () => ({
  type: [],
  partners: [],
  clients: [],
  facilities: [],
  user: [],
  filter_type: [],
  status: [],
  processed_date_range: [startOfMonth(new Date().setHours(0, 0, 0, 0)), new Date().setHours(23, 59, 59, 59)],
  start_time_range: "",
  end_time_range: "",
});

const dateRangeKeys = ["processed_date_range", "start_time_range", "end_time_range"];

const MultiSelectFilter = ({ name, options, placeholder, formik }) => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <div style={{ width: '180px', position: 'relative' }}>
      <Select
        isMulti
        options={options}
        className="select-search"
        classNamePrefix="select"
        placeholder={placeholder}
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={true}
        name={name}
        onChange={(selected) => {
          const values = selected ? selected.map(option => option.value) : [];
          formik.setFieldValue(name, values);
        }}
        value={options.filter(option => formik.values[name]?.includes(option.value))}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        styles={{
          ...selectStyles,
          control: (styles, state) => ({
            ...selectStyles.control(styles, state),
            maxHeight: isFocused ? "auto" : "30px",
          }),
          valueContainer: (styles, state) => ({
            ...selectStyles.valueContainer(styles, state),
            maxHeight: isFocused ? "auto" : "30px",
            overflow: isFocused ? "visible" : "scroll",
          }),
        }}
      />
    </div>
  );
};

function SuperQaReport() {
  const dispatch = useDispatch();
  const pageLocation = useLocation();
  const pageKey = pageLocation.pathname.split('/')[1] || 'super-qa-report';
  const [filter, updateFilter, clearCacheFilters] = useCacheFilter(pageKey, '');

  const [pageno, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [colorRow, setColorRow] = useState({});
  const [data, setData] = useState({});
  const [limit, setLimit] = useState(100);
  const [searchedData, setSearchedData] = useState(getInitialFilterValues);
  const [isInitSearch, setIsInitSearch] = useState(true);
  const [userList, setUserList] = useState([]);

  const superQaReportService = new SuperQaReportService();
  const { getSuperQaReport } = superQaReportService;

  const dataListService = new DataListService();
  const { getUserlist } = dataListService;

  useEffect(() => {
    getUserlist(1, "").then((response) => {
      if (response?.status && response?.data?.results) {
        setUserList(response.data.results);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (row) => {
    setColorRow(row);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLimit(newPerPage);
    setPageNo(1);
    fetchData(1, newPerPage, searchedData);
    updateFilter({
      defaultValue: {
        ...searchedData,
        pageno: 1,
        limit: newPerPage,
        isInitSearch
      }
    });
  };

  const handlePageChange = (page) => {
    setPageNo(page);
    fetchData(page, limit, searchedData);
    updateFilter({
      defaultValue: {
        ...searchedData,
        pageno: page,
        limit,
        isInitSearch
      }
    });
  };

  const fetchData = (page, limit, values, export_type = false, header_names = []) => {
    if (export_type) {
      setExportLoading(true);
    } else {
    setLoading(true);
    }

    const processed_date_range = values.processed_date_range ? convertDateToStr(values.processed_date_range, false) : "";
    const start_time_range = values.start_time_range ? convertDateToStr(values.start_time_range, true) : "";
    const end_time_range = values.end_time_range ? convertDateToStr(values.end_time_range, true) : "";

    getSuperQaReport({
      page,
      limit,
      partners: values.partners,
      clients: values.clients,
      facilities: values.facilities,
      user: values.user,
      filter_type: values.filter_type,
      status: values.status,
      processed_date_range,
      start_time_range,
      end_time_range,
      export_type,
      header_names
    }).then((response) => { 
      if (export_type) {
        setExportLoading(false);
        const url = response?.data?.export_file || response?.data?.url || response?.data;
        if (response?.status !== false && typeof url === "string" && /^https?:\/\//i.test(url)) {
          downloadXl(url);
        } else {
          const message = response?.message || response?.data?.message || (typeof response?.data === "string" ? response?.data : "") || "No data to export";
          dispatch(seErrorMessage({ message, is_show: true }));
        }
      } else {
      setData(response?.data || {});
      setLimit(limit);
      setPageNo(page);
      setTotalRows(response?.data?.count ?? (Array.isArray(response?.data) ? response?.data.length : (response?.data?.results?.length || 0)));
      setLoading(false);
      }
    }).catch(() => {
      setLoading(false);
      setExportLoading(false);
      dispatch(seErrorMessage({ message: "An error occurred while fetching reports data.", is_show: true }));
    });
  };

  const searchFormik = useFormik({
    initialValues: getInitialFilterValues(),
    onSubmit: (values) => {
      setIsInitSearch(false);
      setSearchedData(values);
      fetchData(1, limit, values);
      updateFilter({
        defaultValue: {
          ...values,
          pageno: 1,
          limit,
          isInitSearch: false
        }
      });
    }
  });

  const handleReset = () => {
    clearCacheFilters();
    const defaultValues = getInitialFilterValues();
    searchFormik.resetForm({ values: defaultValues });
    setIsInitSearch(true);
    setData({});
    setTotalRows(0);
    setPageNo(1);
    setSearchedData(defaultValues);
  };

  const handleOnDateTimeChange = ({ name, value }) => {
    searchFormik.setFieldValue(name, value ?? "");
  };

  // Restore cached filter state on navigation
  useEffect(() => {
    if (
      filter?.filterValue?.defaultValue &&
      Object.keys(filter?.filterValue?.defaultValue).length > 0
    ) {
      const cached = filter.filterValue.defaultValue;
      const { pageno: cachedPage, limit: cachedLimit, isInitSearch: cachedInit, ...formValues } = cached;

      const restoredPage = Number(cachedPage) || 1;
      const restoredLimit = Number(cachedLimit) || 100;
      setPageNo(restoredPage);
      setLimit(restoredLimit);

      const parsedValues = { ...getInitialFilterValues(), ...formValues };
      dateRangeKeys.forEach((key) => {
        if (Array.isArray(parsedValues[key])) {
          parsedValues[key] = [
            parsedValues[key][0] ? new Date(parsedValues[key][0]) : null,
            parsedValues[key][1] ? new Date(parsedValues[key][1]) : null,
          ];
        }
      });

      searchFormik.setValues(parsedValues);
      setSearchedData(parsedValues);

      if (cachedInit === false) {
        setIsInitSearch(false);
        fetchData(restoredPage, restoredLimit, parsedValues);
      }
      clearCacheFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'S.NO',
        name: 'S.NO',
        selector: (row, index) =>
          Number(pageno) > 1
            ? (Number(pageno) - 1) * Number(limit) + index + 1
            : index + 1,
        wrap: true,
        width: "60px",
        grow: 0.1,
      },
      {
        id: 'username',
        name: 'Username',
        exportKey: 'USERNAME',
        selector: row => row?.username,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.username}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'partners',
        name: 'Partners',
        exportKey: 'PARTNERS',
        selector: row => row?.partners,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.partners}</span>,
        left: true,
        wrap: true,
        width: "130px",
      },
      {
        id: 'clients',
        name: 'Clients',
        exportKey: 'CLIENTS',
        selector: row => row?.clients,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.clients}</span>,
        left: true,
        wrap: true,
        width: "130px",
      },
      {
        id: 'facilities',
        name: 'Facilities',
        exportKey: 'FACILITIES',
        selector: row => row?.facilities,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.facilities}</span>,
        left: true,
        wrap: true,
        width: "130px",
      },
      {
        id: 'filter_type',
        name: 'Filter Type',
        exportKey: 'FILTER TYPE',
        selector: row => row?.filter_type,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.filter_type}</span>,
        left: true,
        wrap: true,
        width: "110px",
      },
      {
        id: 'fields',
        name: 'Selected Fields',
        exportKey: 'FIELDS',
        selector: row => row?.fields,
        cell: (row) => (
          <Tooltip disableInteractive title={row?.fields || ""} placement="top" arrow>
            <div onClick={() => handleRowClick(row)} className='text-truncate' style={{ width: "100%", maxWidth: "170px" }}>
              {row?.fields}
            </div>
          </Tooltip>
        ),
        left: true,
        width: "180px",
      },
      {
        id: 'filters',
        name: 'Applied Filters',
        exportKey: 'FILTERS',
        selector: row => row?.filters,
        cell: (row) => (
          <Tooltip disableInteractive title={row?.filters || ""} placement="top" arrow>
            <div onClick={() => handleRowClick(row)} className='text-truncate' style={{ width: "100%", maxWidth: "170px" }}>
              {row?.filters}
            </div>
          </Tooltip>
        ),
        left: true,
        width: "180px",
      },
      {
        id: 'record_count',
        name: 'Record Count',
        exportKey: 'RECORD COUNT',
        selector: row => row?.record_count,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.record_count}</span>,
        left: true,
        wrap: true,
        width: "110px",
      },
      {
        id: 'job_count',
        name: 'Job Count',
        exportKey: 'JOB COUNT',
        selector: row => row?.job_count,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.job_count}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'reassigned_count',
        name: 'Reassigned Count',
        exportKey: 'REASSIGNED COUNT',
        selector: row => row?.reassigned_count,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.reassigned_count}</span>,
        left: true,
        wrap: true,
        width: "130px",
      },
      {
        id: 'reassigned_jobs',
        name: 'Reassigned Jobs',
        exportKey: 'REASSIGNED JOBS',
        selector: row => row?.reassigned_jobs,
        cell: (row) => (
          <Tooltip disableInteractive title={row?.reassigned_jobs || ""} placement="top" arrow>
            <div onClick={() => handleRowClick(row)} className='text-truncate' style={{ width: "100%", maxWidth: "140px" }}>
              {row?.reassigned_jobs}
            </div>
          </Tooltip>
        ),
        left: true,
        width: "150px",
      },
      {
        id: 'status',
        name: 'Status',
        exportKey: 'STATUS',
        selector: row => row?.status,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.status}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'processed_date',
        name: 'Processed Date',
        exportKey: 'PROCESSED DATE',
        selector: row => row?.processed_date,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.processed_date}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'start_time',
        name: 'Start Time',
        exportKey: 'START TIME',
        selector: row => row?.start_time,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.start_time}</span>,
        left: true,
        wrap: true,
        width: "160px",
      },
      {
        id: 'end_time',
        name: 'End Time',
        exportKey: 'END TIME',
        selector: row => row?.end_time,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.end_time}</span>,
        left: true,
        wrap: true,
        width: "160px",
      },
      {
        id: 'total_time',
        name: 'Total Time',
        exportKey: 'TOTAL TIME',
        selector: row => row?.total_time,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.total_time}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'audit_type',
        name: 'Audit Type',
        exportKey: 'AUDIT TYPE',
        selector: row => row?.audit_type,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.audit_type}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
    ],
    [limit, pageno]
  );

  const handleExport = () => {
    const headers = columns.filter(i => i.exportKey !== undefined).map(i => i.exportKey);
    fetchData(pageno, limit, searchFormik.values, true, headers);
  };

  const conditionalRowStyles = [
    {
      when: (row) => (row?.id ? colorRow?.id === row?.id : colorRow === row),
      style: () => ({
        backgroundColor: '#dbf4f5',
        color: 'black',
      }),
    },
  ];

  const renderDateRange = (name, title, withTime) => (
    <Tooltip disableInteractive title={title} placement="top" arrow>
      <div className="correspondenceDatePicker">
        <Stack direction="column" spacing={8} alignItems="flex-start">
          <DateRangePicker
            ranges={predefinedBottomRanges}
            {...(withTime ? { defaultCalendarValue: [new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)] } : {})}
            cleanable={true}
            placement="auto"
            appearance="default"
            format={withTime ? "MM/dd/yyyy HH:mm:ss" : "MM/dd/yyyy"}
            className="form-date-control form-control-solid b-r-1 text-dark"
            placeholder={"MM/DD/YYYY  - MM/DD/YYYY "}
            name={name}
            onChange={(e) => { handleOnDateTimeChange({ name, value: e }) }}
            value={searchFormik?.values?.[name] || null}
          />
        </Stack>
      </div>
    </Tooltip>
  );

  return (
    <React.Fragment>
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle tab-fixed-header">
          <h1 id='page-title'>Super QA Report</h1>
        </div>

        <section className="section mt-5 ">
          <div className="row align-items-top py-2 gap-2 gap-md-5">
            <div className="col-lg-12">
              <div className="card mb-5 mb-xl-8 mt-3">
                <Form onSubmit={searchFormik.handleSubmit} onReset={handleReset}>
                  <div className=" ">
                    <div className="px-1 py-1 d-flex pe-2 flex-row gap-2 align-items-center flex-wrap">
                      <MultiSelectFilter name="user" options={userList} placeholder="User" formik={searchFormik} />
                      <div>
                        <PartnerDropDown lockboxformik={searchFormik} width="210px" />

                      </div>
                      <div>
                        <ClientDropDown lockboxformik={searchFormik} width="210px" />

                      </div>

                      <div>
                        <FacilityDropDown lockboxformik={searchFormik} width="210px" />

                      </div>
                      <MultiSelectFilter name="filter_type" options={filterTypes} placeholder="Filter Type" formik={searchFormik} />
                      <MultiSelectFilter name="status" options={statuses} placeholder="Select Status" formik={searchFormik} />

                      {renderDateRange("processed_date_range", "Processed Date Range", false)}
                      {renderDateRange("start_time_range", "Start Time Range", true)}
                      {renderDateRange("end_time_range", "End Time Range", true)}

                      <div>
                        <Tooltip disableInteractive title={"Search"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="submit" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              {loading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <BiSearch />
                              )}
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Reset"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="reset" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              <BiReset />
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Download"} placement="top" arrow>
                          <Button
                            className="btn btn-light btn-active-primary"
                            type="button"
                            onClick={handleExport}
                            disabled={exportLoading || loading}
                          >
                            {exportLoading ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                            <span className="svg-icon svg-icon-3">
                              <BsCloudDownloadFill />
                            </span>
                            )}
                          </Button>
                        </Tooltip>
                      </div>

                    </div>
                  </div>
                </Form>

                <div className="card-body p-common">
                  <div className="table-responsive">
                    <div className="dashboardtable">
                      <DataTable
                        className="custom-datatable"
                        paginationDefaultPage={pageno}
                        fixedHeader={true}
                        progressPending={loading}
                        data={Array.isArray(data?.results) ? data?.results : (Array.isArray(data) ? data : [])}
                        columns={columns}
                        pointerOnHover={true}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={limit}
                        paginationRowsPerPageOptions={[25, 50, 100, 150, 200]}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        conditionalRowStyles={conditionalRowStyles}
                        theme="solarized"
                        customStyles={datatableCustomStyles}
                        striped={true}
                        noDataComponent={isInitSearch ? <p className="p-4">Apply filters to refine and display the data</p> : <p className="p-4">There are no records to display</p>}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
}

export default SuperQaReport;
